import { type Hex, hexKey, neighbours } from '../core/hex';

/**
 * Breadth-first shortest path over the axial hex grid (HEX-70 auto-walk).
 *
 * Uniform step cost (every hex hop is 1), so BFS yields a shortest path in hex
 * count — no need for A* weighting. The grid is unbounded in principle; the
 * `isWalkable` predicate is what bounds the search. Callers pass a predicate
 * restricted to known/revealed passable tiles, so the reachable frontier is
 * finite and BFS terminates.
 *
 * `start` is always treated as walkable (it is where the player stands) — the
 * predicate is only consulted for candidate hexes the search expands into,
 * including `goal`. The returned path includes both endpoints: `[start, …,
 * goal]`. A single-hex result (`start === goal`) returns `[start]`.
 *
 * Returns `null` when `goal` is not walkable or no walkable chain connects the
 * two — callers treat that as "unreachable through known space" and no-op.
 *
 * `maxExplored` is a defence-in-depth cap against a predicate that mistakenly
 * admits an unbounded region; under the intended bounded predicate it is never
 * reached.
 */
export function findPath(
  start: Hex,
  goal: Hex,
  isWalkable: (hex: Hex) => boolean,
  maxExplored = 10_000,
): Hex[] | null {
  const startKey = hexKey(start);
  const goalKey = hexKey(goal);
  if (startKey === goalKey) return [start];
  if (!isWalkable(goal)) return null;

  const visited = new Set<string>([startKey]);
  const prev = new Map<string, Hex>();
  const queue: Hex[] = [start];
  let head = 0;
  let explored = 0;

  while (head < queue.length) {
    const cur = queue[head++] as Hex;
    if (++explored > maxExplored) return null;
    const curKey = hexKey(cur);
    if (curKey === goalKey) return reconstruct(prev, start, startKey, cur, curKey);
    for (const nb of neighbours(cur)) {
      const nbKey = hexKey(nb);
      if (visited.has(nbKey)) continue;
      if (!isWalkable(nb)) continue;
      visited.add(nbKey);
      prev.set(nbKey, cur);
      queue.push(nb);
    }
  }
  return null;
}

/**
 * Multi-segment path through an ordered list of waypoints (HEX-74). Chains
 * `findPath` across `start → waypoints[0] → … → waypoints[n-1]`, concatenating
 * the segments and dropping the duplicated joint hex between them. The result
 * is a single contiguous walk `[start, …, lastWaypoint]`.
 *
 * Returns `null` if ANY segment is unreachable — a chain is only as good as its
 * weakest leg. With an empty `waypoints` list the path is just `[start]`.
 *
 * Used by the worldview waypoint preview (shift-click): each shift-click appends
 * a waypoint and the whole chain is re-previewed; a plain click commits it.
 */
export function findPathThrough(
  start: Hex,
  waypoints: readonly Hex[],
  isWalkable: (hex: Hex) => boolean,
  maxExplored = 10_000,
): Hex[] | null {
  const full: Hex[] = [start];
  let from = start;
  for (const wp of waypoints) {
    const seg = findPath(from, wp, isWalkable, maxExplored);
    if (!seg) return null;
    // `seg` is `[from, …, wp]`; append all but the leading joint (it equals the
    // previous segment's tail / `start`).
    for (let i = 1; i < seg.length; i++) full.push(seg[i] as Hex);
    from = wp;
  }
  return full;
}

function reconstruct(
  prev: Map<string, Hex>,
  start: Hex,
  startKey: string,
  end: Hex,
  endKey: string,
): Hex[] {
  const path: Hex[] = [];
  let node: Hex | undefined = end;
  let key = endKey;
  while (node) {
    path.push(node);
    if (key === startKey) break;
    node = prev.get(key);
    if (!node) {
      // Unreachable in practice — every enqueued non-start hex has a `prev`.
      path.push(start);
      break;
    }
    key = hexKey(node);
  }
  path.reverse();
  return path;
}
